import { Record as record, List } from 'immutable';
import Joi from 'joi';
import _ from 'lodash';

import RoversRangersTicketsService from '../services/RoversRangersTicketsService';
import { sum } from '../helpers/Immutable';
import config from '../config';
import TicketType from './TicketType';
import PassengerRailcard from './PassengerRailcard';
import trainTime from '../lib/TrainTime';
import { construct, fields, resolve, resolveAll } from '../helpers/LinkMapper';
import ValidationMixin from './ValidationMixin';
import { serialize, serializers, deserialize, deserializers } from '../helpers/SearchUrls';
import validation from '../validation';

function _minDate () {
    return trainTime().add(3, 'days').startOf('day');
}

function _maxDate () {
    return trainTime().add(config.get('maxOutboundDays'), 'days').endOf('day');
}

const defaults = {
    date: _minDate(),
    adults: 1,
    children: 0,
    ticketType: RoversRangersTicketsService.getFirst(),
    railcards: new List()
};

class RoversRangersSearch extends record(defaults) {
    static fromJSON (json = {}) {
        json = _.defaults(json, { adults: 0, children: 0 });
        return construct(RoversRangersSearch, json, {
            date: fields.datetime,
            adults: fields.number,
            children: fields.number,
            ticketType: resolve.as(TicketType),
            railcards: resolveAll.as(PassengerRailcard)
        });
    }

    serialize () {
        return serialize({
            date: serializers.date,
            adults: serializers.identity,
            children: serializers.identity,
            ticketType: serializers.ticketType,
            railcards: serializers.railcards
        }, this);
    }

    /**
     * Return a promise that resolves to a new RoversRangersSearch, from the url params
     */
    static deserialize (source) {
        const schema = {
            date: deserializers.date,
            adults: deserializers.int,
            children: deserializers.int,
            ticketType: deserializers.ticketType,
            railcards: deserializers.railcards
        };

        return deserialize(schema, source).then((deserialized) => {
            return new RoversRangersSearch(deserialized);
        });
    }

    get minimumDate () {
        return _minDate();
    }

    get maximumDate () {
        return _maxDate();
    }

    refreshDates () {
        if (this.date.isBefore(_minDate())) {
            return this.set('date', _minDate());
        }
        return this;
    }

    getComputedValues () {
        return {
            date: this.date.format(),
            ticketTypeCode: this.ticketType.code,
            passengersNumber: this.adults + this.children,
            railcardsNumber: this.railcards ? sum(this.railcards, 'quantity') : 0
        };
    }

    isValid () {
        return this.isComplete();
    }

    getSchema () {
        return {
            date: Joi.date().min(_minDate().format()).max(_maxDate().format()).required(),
            ticketTypeCode: validation.requiredString(3, 3),
            passengersNumber: Joi.number().min(1).max(9).required(),
            railcardsNumber: validation.railcardsNumber('passengersNumber')
        };
    }
}

Object.assign(RoversRangersSearch.prototype, ValidationMixin);

export default RoversRangersSearch;
